import { IncomingMessage, MessageHandler } from '@carvana/merch-native';
import { useCallback, useContext, useMemo } from 'react';
import ModalContext from '~/context/vdp/ModalContext';
import { NativeModalId } from '~/context/vdp/ModalContext/constants';
import { logError } from '~/utilities/loggers';
import { ListenerEvent, type ListenerEventType } from './constants';

type CloseModalMessage = IncomingMessage<
  ListenerEventType,
  { modal: NativeModalId }
>;

function isCloseModalMessage(
  message: IncomingMessage<ListenerEventType, any>
): message is CloseModalMessage {
  return (
    message.event === ListenerEvent.CloseModal && message.payload.modal != null
  );
}

function useHandleCloseModal(): (modal: NativeModalId) => void {
  const {
    setGeolocationModalOpen,
    setReservableCancelModalOpen,
    setReturnPolicyModalOpen
  } = useContext(ModalContext);

  return useCallback(
    (modal: NativeModalId) => {
      switch (modal) {
        case NativeModalId.Geolocation:
          setGeolocationModalOpen(false);
          break;
        case NativeModalId.CancelReservation:
          setReservableCancelModalOpen(false);
          break;
        case NativeModalId.ReturnPolicy:
          setReturnPolicyModalOpen(false);
          break;
        default:
          logError(new Error('Unknown modal'), { modal });
      }
    },
    [
      setGeolocationModalOpen,
      setReservableCancelModalOpen,
      setReturnPolicyModalOpen
    ]
  );
}

export default function useCloseModalHandler(): MessageHandler<ListenerEventType> {
  const handleCloseModal = useHandleCloseModal();

  return useMemo(
    () =>
      new MessageHandler(isCloseModalMessage, (message: CloseModalMessage) => {
        handleCloseModal(message.payload.modal);
      }),
    [handleCloseModal]
  );
}
